import { Chromosome, Particle } from '../types/algorithm';
import { OptimizationParameters, OptimizationResults, OptimizationResultsWithStats, AlgorithmResult, ChartDataPoint } from '../types';
import { parseQWSDataset, buildQualityMatrix } from '../utils/qwsParser';
import { calculateResponseTime, calculateEnergy, calculateCost, FitnessCalculator, setGlobalFitnessCalculator, clearGlobalFitnessCalculator } from '../utils/fitnessCalculator';
import { runGeneticAlgorithm } from '../algorithms/geneticAlgorithm';
import { runCulturalAlgorithm } from '../algorithms/culturalAlgorithm';
import { runSFLA } from '../algorithms/sfla';
import { runSFLAGA } from '../algorithms/sflaga';
import { runSFLACA } from '../algorithms/sflaca';
import { runPSO } from '../algorithms/pso';
import { runGAPSO } from '../algorithms/gapso';
import { calculateAlgorithmStatistics } from '../utils/statistics';
import { normalizeAcrossAlgorithms } from '../utils/normalization';
import { calculateCompositeScoreStatistics, DEFAULT_COMPOSITE_WEIGHTS } from '../utils/compositeScore';
import { performParetoAnalysis } from '../utils/paretoAnalysis';

export interface ConvergenceData {
  ga: ChartDataPoint[];
  culture: ChartDataPoint[];
  frog: ChartDataPoint[];
  sflaga: ChartDataPoint[];
  sflaca: ChartDataPoint[];
  pso: ChartDataPoint[];
  gapso: ChartDataPoint[];
}

export class OptimizationService {
  private matrix: number[][][] | null = null;
  private services: any[] = [];
  private taskCount: number = 0;
  private serviceCount: number = 0;
  private fitnessCalculator: FitnessCalculator | null = null;

  /**
   * Loads and parses QWS dataset
   */
  loadDataset(csvText: string, taskCount: number): void {
    this.services = parseQWSDataset(csvText);
    this.serviceCount = this.services.length;
    this.taskCount = taskCount;
    this.matrix = buildQualityMatrix(this.services, taskCount);
    
    // Initialize fitness calculator with cached min/max values (PERFORMANCE OPTIMIZATION)
    if (this.matrix) {
      this.fitnessCalculator = new FitnessCalculator();
      this.fitnessCalculator.initialize(this.matrix, taskCount, this.serviceCount);
      setGlobalFitnessCalculator(this.fitnessCalculator);
    }
  }

  /**
   * Runs all optimization algorithms
   */
  async runOptimization(
    parameters: OptimizationParameters,
    onAlgorithmStart?: (algorithm: string) => void,
    onGenerationUpdate?: (algorithm: string, generation: number, fitness: number) => void,
    onAlgorithmComplete?: (algorithm: string, result: AlgorithmResult, partialResults: Partial<OptimizationResults>) => void,
    shouldCancel?: () => boolean
  ): Promise<{
    results: OptimizationResults;
    convergenceData: ConvergenceData;
  }> {
    if (!this.matrix) {
      throw new Error('Dataset not loaded');
    }

    const convergenceData: ConvergenceData = {
      ga: [],
      culture: [],
      frog: [],
      sflaga: [],
      sflaca: [],
      pso: [],
      gapso: [],
    };

    // Check for cancellation before starting GA
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run GA
    if (onAlgorithmStart) onAlgorithmStart('GA');
    const gaStartTime = performance.now();
    const gaResult = await runGeneticAlgorithm(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        selectionRate: parameters.selectionRate,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
      },
      (gen, fitness) => {
        convergenceData.ga.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('GA', gen, fitness);
      }
    );
    const gaExecutionTime = performance.now() - gaStartTime;
    
    // Convert and notify GA completion
    const convertChromosomeToResult = (chrom: Chromosome, execTime: number): AlgorithmResult => ({
      fitness: chrom.fitness,
      solution: chrom.x,
      energy: calculateEnergy(chrom.x, this.matrix!, this.taskCount, this.serviceCount),
      cost: calculateCost(chrom.x, this.matrix!, this.taskCount, this.serviceCount),
      responseTime: calculateResponseTime(chrom.x, this.matrix!, this.taskCount, this.serviceCount),
      executionTime: execTime,
    });
    
    const gaResultConverted = convertChromosomeToResult(gaResult, gaExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('GA', gaResultConverted, { ga: gaResultConverted });
    }

    // Check for cancellation before starting Cultural Algorithm
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run Cultural Algorithm
    if (onAlgorithmStart) onAlgorithmStart('Cultural');
    const cultureStartTime = performance.now();
    const cultureResult = await runCulturalAlgorithm(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        selectionRate: parameters.selectionRate,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
      },
      (gen, fitness) => {
        convergenceData.culture.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('Cultural', gen, fitness);
      }
    );
    const cultureExecutionTime = performance.now() - cultureStartTime;
    
    const cultureResultConverted = convertChromosomeToResult(cultureResult, cultureExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('Cultural', cultureResultConverted, { ga: gaResultConverted, culture: cultureResultConverted });
    }

    // Check for cancellation before starting SFLA
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run SFLA
    if (onAlgorithmStart) onAlgorithmStart('SFLA');
    const frogStartTime = performance.now();
    const frogResult = await runSFLA(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        memeplexCount: parameters.memeplexCount,
      },
      (gen, fitness) => {
        convergenceData.frog.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('SFLA', gen, fitness);
      }
    );
    const frogExecutionTime = performance.now() - frogStartTime;
    
    const frogResultConverted = convertChromosomeToResult(frogResult, frogExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('SFLA', frogResultConverted, { ga: gaResultConverted, culture: cultureResultConverted, frog: frogResultConverted });
    }

    // Check for cancellation before starting SFLAGA
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run SFLAGA
    if (onAlgorithmStart) onAlgorithmStart('SFLAGA');
    const sflagaStartTime = performance.now();
    const sflagaResult = await runSFLAGA(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        memeplexCount: parameters.memeplexCount,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
      },
      (gen, fitness) => {
        convergenceData.sflaga.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('SFLAGA', gen, fitness);
      }
    );
    const sflagaExecutionTime = performance.now() - sflagaStartTime;
    
    const sflagaResultConverted = convertChromosomeToResult(sflagaResult, sflagaExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('SFLAGA', sflagaResultConverted, { 
        ga: gaResultConverted, 
        culture: cultureResultConverted, 
        frog: frogResultConverted,
        sflaga: sflagaResultConverted 
      });
    }

    // Check for cancellation before starting SFLACA
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run SFLACA
    if (onAlgorithmStart) onAlgorithmStart('SFLACA');
    const sflacaStartTime = performance.now();
    const sflacaResult = await runSFLACA(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        memeplexCount: parameters.memeplexCount,
        selectionRate: parameters.selectionRate,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
      },
      (gen, fitness) => {
        convergenceData.sflaca.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('SFLACA', gen, fitness);
      }
    );
    const sflacaExecutionTime = performance.now() - sflacaStartTime;
    
    const sflacaResultConverted = convertChromosomeToResult(sflacaResult, sflacaExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('SFLACA', sflacaResultConverted, { 
        ga: gaResultConverted, 
        culture: cultureResultConverted, 
        frog: frogResultConverted,
        sflaga: sflagaResultConverted,
        sflaca: sflacaResultConverted 
      });
    }

    // Check for cancellation before starting PSO
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run PSO
    if (onAlgorithmStart) onAlgorithmStart('PSO');
    const psoStartTime = performance.now();
    const psoResult = await runPSO(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
      },
      (gen, fitness) => {
        convergenceData.pso.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('PSO', gen, fitness);
      }
    );
    const psoExecutionTime = performance.now() - psoStartTime;
    
    const convertParticleToResult = (particle: Particle, execTime: number): AlgorithmResult => ({
      fitness: particle.fitness,
      solution: particle.position,
      energy: calculateEnergy(particle.position, this.matrix!, this.taskCount, this.serviceCount),
      cost: calculateCost(particle.position, this.matrix!, this.taskCount, this.serviceCount),
      responseTime: calculateResponseTime(particle.position, this.matrix!, this.taskCount, this.serviceCount),
      executionTime: execTime,
    });
    
    const psoResultConverted = convertParticleToResult(psoResult, psoExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('PSO', psoResultConverted, { 
        ga: gaResultConverted, 
        culture: cultureResultConverted, 
        frog: frogResultConverted,
        sflaga: sflagaResultConverted,
        sflaca: sflacaResultConverted,
        pso: psoResultConverted 
      });
    }

    // Check for cancellation before starting GAPSO
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run GAPSO
    if (onAlgorithmStart) onAlgorithmStart('GAPSO');
    const gapsoStartTime = performance.now();
    const gapsoResult = await runGAPSO(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        crossoverRate: parameters.crossoverRate,
      },
      (gen, fitness) => {
        convergenceData.gapso.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('GAPSO', gen, fitness);
      }
    );

    const gapsoExecutionTime = performance.now() - gapsoStartTime;
    
    const gapsoResultConverted = convertParticleToResult(gapsoResult, gapsoExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('GAPSO', gapsoResultConverted, { 
        ga: gaResultConverted, 
        culture: cultureResultConverted, 
        frog: frogResultConverted,
        sflaga: sflagaResultConverted,
        sflaca: sflacaResultConverted,
        pso: psoResultConverted,
        gapso: gapsoResultConverted 
      });
    }

    const results: OptimizationResults = {
      ga: convertChromosomeToResult(gaResult, gaExecutionTime),
      culture: convertChromosomeToResult(cultureResult, cultureExecutionTime),
      frog: convertChromosomeToResult(frogResult, frogExecutionTime),
      sflaga: convertChromosomeToResult(sflagaResult, sflagaExecutionTime),
      sflaca: convertChromosomeToResult(sflacaResult, sflacaExecutionTime),
      pso: convertParticleToResult(psoResult, psoExecutionTime),
      gapso: convertParticleToResult(gapsoResult, gapsoExecutionTime),
      configuration: {
        taskCount: parameters.taskCount,
        propertyCount: parameters.qosProperties,
        initialPopulation: parameters.initialPopulation,
        crossover: parameters.crossoverRate,
        mutation: parameters.mutationRate,
      },
    };

    return { results, convergenceData };
  }

  /**
   * Runs optimization multiple times and calculates statistics
   */
  async runOptimizationWithStatistics(
    parameters: OptimizationParameters,
    onAlgorithmStart?: (algorithm: string) => void,
    onGenerationUpdate?: (algorithm: string, generation: number, fitness: number) => void,
    onRunComplete?: (runNumber: number, totalRuns: number, intermediateResults?: OptimizationResultsWithStats) => void,
    onAlgorithmComplete?: (algorithm: string, result: AlgorithmResult, partialResults: Partial<OptimizationResults>) => void,
    shouldCancel?: () => boolean
  ): Promise<{
    results: OptimizationResultsWithStats;
    convergenceData: ConvergenceData;
  }> {
    const numberOfRuns = parameters.numberOfRuns || 1;
    
    if (numberOfRuns === 1) {
      // Single run - no statistics needed
      const { results, convergenceData } = await this.runOptimization(
        parameters,
        onAlgorithmStart,
        onGenerationUpdate,
        onAlgorithmComplete,
        shouldCancel
      );
      return {
        results: {
          ...results,
          timestamp: new Date().toISOString(),
          runNumber: 1,
        },
        convergenceData,
      };
    }

    // Multiple runs - collect results and calculate statistics
    const allRuns: OptimizationResults[] = [];
    let lastConvergenceData: ConvergenceData | null = null;

    for (let run = 0; run < numberOfRuns; run++) {
      // Check for cancellation before starting each run
      if (shouldCancel && shouldCancel()) {
        throw new Error('Optimization cancelled');
      }
      
      const { results, convergenceData } = await this.runOptimization(
        parameters,
        (algorithm) => {
          if (onAlgorithmStart) {
            onAlgorithmStart(`${algorithm} (Run ${run + 1}/${numberOfRuns})`);
          }
        },
        (algorithm, generation, fitness) => {
          if (onGenerationUpdate) {
            // Adjust progress to account for multiple runs
            // Each run contributes (100 / numberOfRuns) to overall progress
            // Within each run, we track generation progress as before
            const runBaseProgress = (run / numberOfRuns) * 100;
            const runProgress = (100 / numberOfRuns) * ((generation / parameters.iterations));
            const totalProgress = runBaseProgress + runProgress;
            // Extract just the algorithm name (remove run info)
            const algName = algorithm.split(' (Run')[0];
            onGenerationUpdate(algName, generation, fitness);
          }
        },
        (algorithm, result, partialResults) => {
          if (onAlgorithmComplete) {
            // Pass through the callback with algorithm name (without run suffix)
            const algName = algorithm.split(' (Run')[0];
            onAlgorithmComplete(algName, result, partialResults);
          }
        },
        shouldCancel
      );

      allRuns.push(results);
      lastConvergenceData = convergenceData; // Store convergence data from last run

      // Calculate intermediate statistics after each run (PROGRESSIVE UPDATE)
      const intermediateStatistics: OptimizationResultsWithStats['statistics'] = {
        ga: calculateAlgorithmStatistics(allRuns.map(r => r.ga)),
        culture: calculateAlgorithmStatistics(allRuns.map(r => r.culture)),
        frog: calculateAlgorithmStatistics(allRuns.map(r => r.frog)),
        sflaga: calculateAlgorithmStatistics(allRuns.map(r => r.sflaga)),
        sflaca: calculateAlgorithmStatistics(allRuns.map(r => r.sflaca)),
        pso: calculateAlgorithmStatistics(allRuns.map(r => r.pso)),
        gapso: calculateAlgorithmStatistics(allRuns.map(r => r.gapso)),
      };

      // Calculate intermediate normalization and composite scores
      const intermediateNormalized = normalizeAcrossAlgorithms(allRuns);
      const intermediateCompositeScores = calculateCompositeScoreStatistics(intermediateNormalized, DEFAULT_COMPOSITE_WEIGHTS);

      // Calculate intermediate Pareto analysis
      const intermediateParetoAnalysis = performParetoAnalysis(allRuns);

      // Create intermediate results with current statistics
      const intermediateResults: OptimizationResultsWithStats = {
        ...results,
        statistics: intermediateStatistics,
        compositeScores: intermediateCompositeScores.perAlgorithm,
        paretoAnalysis: intermediateParetoAnalysis,
        timestamp: new Date().toISOString(),
        runNumber: run + 1,
      };

      // Notify that a run is complete with intermediate results for UI update
      if (onRunComplete) {
        onRunComplete(run + 1, numberOfRuns, intermediateResults);
      }
    }

    // Final statistics (same as last intermediate, but included for clarity)
    const statistics: OptimizationResultsWithStats['statistics'] = {
      ga: calculateAlgorithmStatistics(allRuns.map(r => r.ga)),
      culture: calculateAlgorithmStatistics(allRuns.map(r => r.culture)),
      frog: calculateAlgorithmStatistics(allRuns.map(r => r.frog)),
      sflaga: calculateAlgorithmStatistics(allRuns.map(r => r.sflaga)),
      sflaca: calculateAlgorithmStatistics(allRuns.map(r => r.sflaca)),
      pso: calculateAlgorithmStatistics(allRuns.map(r => r.pso)),
      gapso: calculateAlgorithmStatistics(allRuns.map(r => r.gapso)),
    };

    // Calculate normalization and composite scores
    const normalizedResults = normalizeAcrossAlgorithms(allRuns);
    const compositeScores = calculateCompositeScoreStatistics(normalizedResults, DEFAULT_COMPOSITE_WEIGHTS);

    // Perform Pareto analysis
    const paretoAnalysis = performParetoAnalysis(allRuns);

    // Use the last run's results as the representative result
    const lastRun = allRuns[allRuns.length - 1];

    return {
      results: {
        ...lastRun,
        statistics,
        compositeScores: compositeScores.perAlgorithm,
        paretoAnalysis,
        timestamp: new Date().toISOString(),
        runNumber: numberOfRuns,
      },
      convergenceData: lastConvergenceData!,
    };
  }
}

